import { DataProvider } from "@refinedev/core";
import { mockDataProvider } from "./mock";

const V = (import.meta as any).env || {};
export const API = V?.VITE_API_URL || V?.VITE_API_BASE_URL || "http://localhost:8001";
const USE_MOCK = V?.VITE_USE_MOCK === "true";

async function http<T>(url: string, init?: RequestInit): Promise<T> {
  const token = localStorage.getItem('token');
  const headers: any = { 'Content-Type': 'application/json', ...(init?.headers||{}) };
  if (token) headers['Authorization'] = `Bearer ${token}`;
  const res = await fetch(url, { ...init, headers });

  if (!res.ok) {
    const text = await res.text();
    let errorMessage = text;

    // Пытаемся распарсить JSON и извлечь detail
    try {
      const errorData = JSON.parse(text);
      if (errorData.detail) {
        errorMessage = errorData.detail;
      }
    } catch (e) {
      // Если не JSON, используем текст как есть
    }

    throw new Error(errorMessage);
  }

  return res.json();
}

// Resource name mapping: UI resource name -> API endpoint name
const resourceMapping: Record<string, string> = {
  'strategies': 'routing-rules',
  'knowledge-base': 'knowledge-base',
  'stop-words': 'stop-words',
  'stop-word-categories': 'stop-word-categories',
  'templates': 'templates',
  'reviews': 'reviews',
  'responses': 'responses',
  'settings/ai-styles': 'ai-styles',
};

// Helper to get API resource name
const getApiResource = (resource: string): string => resourceMapping[resource] || resource;

const realDataProvider: DataProvider = {
  getList: async ({ resource, pagination, filters, sorters, meta }) => {
    const apiResource = getApiResource(resource);
    const params = new URLSearchParams();

    // Обработка пагинации: поддержка как current, так и currentPage
    if (pagination && pagination.mode !== 'off') {
      const current = pagination.current ?? pagination.currentPage ?? 1;
      const pageSize = pagination.pageSize ?? 20;
      params.set('page', String(current));
      params.set('page_size', String(pageSize));
    }

    // Обработка сортировки
    if (sorters && sorters.length > 0) {
      const sorter = sorters[0];
      if (sorter?.field) {
        params.set('_sort', String(sorter.field));
        params.set('_order', String(sorter.order || 'asc').toUpperCase());
      }
    }

    // Обработка фильтров
    if (filters) {
      for (const f of filters as any) {
        if (f.field) {
          // Обработка операторов null/nnull для фильтрации по наличию/отсутствию значения
          if (f.operator === 'nnull') {
            params.set(String(f.field), 'nnull');
          } else if (f.operator === 'null') {
            params.set(String(f.field), 'null');
          } else if (f.value != null) {
            params.set(String(f.field), String(f.value));
          }
        }
      }
    }

    const data: any = await http(`${API}/api/v1/${apiResource}?${params.toString()}`);
    return { data: data.items || [], total: data.total || 0 } as any;
  },
  getOne: async ({ resource, id }) => {
    const apiResource = getApiResource(resource);
    console.log(`🔍 [DataProvider.getOne] resource: ${resource}, apiResource: ${apiResource}, id: ${id}`);
    const data: any = await http(`${API}/api/v1/${apiResource}/${id}`);
    console.log(`🔍 [DataProvider.getOne] Полученные данные:`, JSON.stringify(data, null, 2));
    return { data } as any;
  },
  update: async ({ resource, id, variables }) => {
    const apiResource = getApiResource(resource);
    const data: any = await http(`${API}/api/v1/${apiResource}/${id}`, { method:'PATCH', body: JSON.stringify(variables) });
    return { data } as any;
  },
  create: async ({ resource, variables }) => {
    const apiResource = getApiResource(resource);
    const data: any = await http(`${API}/api/v1/${apiResource}`, { method:'POST', body: JSON.stringify(variables) });
    return { data } as any;
  },
  deleteOne: async ({ resource, id }) => {
    const apiResource = getApiResource(resource);
    const data: any = await http(`${API}/api/v1/${apiResource}/${id}`, { method: 'DELETE' });
    return { data } as any;
  },
  getApiUrl: () => API,
} as any;

// Export the appropriate provider based on environment variable
export const dataProvider: DataProvider = USE_MOCK ? mockDataProvider : realDataProvider;

// Log which provider is being used (for development)
console.log(`[DataProvider] Using ${USE_MOCK ? "MOCK" : "REAL"} data provider`);
